import { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Share2, 
  Printer,
  ZoomIn,
  ZoomOut,
  Smartphone,
  Zap,
  Loader2
} from "lucide-react";

// Configurar worker de PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function MobilePDFViewer({ 
  pdfUrl, 
  title = "Documento PDF",
  filename = "documento.pdf",
  onPrint = null, // Callback opcional para ejecutar después de imprimir (ej: cerrar modal)
  initialScale = null, // Escala inicial personalizada (ej: 2.0 para etiquetas)
  isLabel = false, // Si es true, usa zoom más alto por defecto para etiquetas
  onDirectPrint = null // Callback para impresión directa con QZ Tray
}) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [shareSupported, setShareSupported] = useState(false);
  const [directPrinting, setDirectPrinting] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      
      setIsMobile(isMobileDevice || isSmallScreen);
      setShareSupported(!!navigator.share && !!navigator.canShare);
      
      // Determinar escala inicial
      if (initialScale) {
        // Si se especificó una escala personalizada, usarla
        setScale(initialScale);
      } else if (isLabel) {
        // Para etiquetas, usar zoom más alto para mejor visibilidad
        setScale(isMobileDevice || isSmallScreen ? 1.5 : 2.0);
      } else if (isMobileDevice || isSmallScreen) {
        // Documentos normales en móvil
        setScale(0.6);
      } else {
        // Documentos normales en escritorio
        setScale(1.0);
      }
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, [initialScale, isLabel]);

  useEffect(() => {
    if (pdfUrl) {
      fetch(pdfUrl)
        .then(res => res.blob())
        .then(blob => setPdfBlob(blob))
        .catch(err => console.error("Error loading PDF:", err));
    }
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfLoaded(true);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.4));

  const handleDownload = () => {
    if (pdfBlob) {
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleShare = async () => {
    if (!pdfBlob) {
      handleDownload();
      return;
    }
    try {
      if (navigator.share && navigator.canShare) {
        const file = new File([pdfBlob], filename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: title,
            text: `${title} - Selecciona tu app de impresión`,
            files: [file]
          });
          // Cerrar modal después de compartir
          if (onPrint) {
            setTimeout(() => onPrint(), 300);
          }
          return;
        }
      }
      handleDownload();
    } catch (err) {
      if (err.name !== 'AbortError') {
        handleDownload();
      }
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.print(), 500);
        };
      }
      // Cerrar el modal después de abrir la ventana de impresión
      if (onPrint) {
        setTimeout(() => onPrint(), 300);
      }
    }
  };

  const handleDirectPrint = async () => {
    if (!onDirectPrint || !pdfUrl) return;
    setDirectPrinting(true);
    try {
      const result = await onDirectPrint(pdfUrl);
      if (result?.success) {
        toast.success(result.message || "Enviado a impresora");
        if (onPrint) setTimeout(() => onPrint(), 300);
      } else {
        toast.error(result?.message || "Error al imprimir");
      }
    } catch (err) {
      toast.error(err.message || "Error de conexión con QZ Tray");
    } finally {
      setDirectPrinting(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ height: '70vh', maxHeight: '70vh' }}>
      {/* Estilos CSS para eliminar cualquier fondo blanco */}
      <style>{`
        .react-pdf__Document,
        .react-pdf__Page,
        .react-pdf__Page__canvas {
          background: transparent !important;
        }
        .react-pdf__message {
          display: none !important;
        }
      `}</style>
      
      {/* Barra de herramientas - SIEMPRE VISIBLE */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-gray-50 border-b shrink-0">
        {/* Navegación de páginas */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={goToPrevPage} disabled={pageNumber <= 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm px-2 min-w-[70px] text-center">
            {pageNumber} / {numPages || '?'}
          </span>
          <Button variant="ghost" size="sm" onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={zoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm min-w-[45px] text-center">{Math.round(scale * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={zoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        {/* Botones de acción */}
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1" data-testid="pdf-download-btn">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Descargar</span>
          </Button>
          {isMobile ? (
            <Button size="sm" onClick={handleShare} className="gap-1 bg-green-600 hover:bg-green-700" data-testid="pdf-print-btn">
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Imprimir</span>
            </Button>
          ) : (
            <Button size="sm" onClick={handlePrint} className="gap-1" data-testid="pdf-print-btn">
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Imprimir</span>
            </Button>
          )}
          {onDirectPrint && (
            <Button 
              size="sm" 
              onClick={handleDirectPrint} 
              disabled={directPrinting}
              className="gap-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="pdf-direct-print-btn"
            >
              {directPrinting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Zap className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Directo</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mensaje para móviles */}
      {isMobile && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 text-xs border-b shrink-0">
          <Smartphone className="h-4 w-4 shrink-0" />
          <span>Para imprimir: usa el botón <strong>Imprimir</strong> y selecciona tu app de impresión</span>
        </div>
      )}

      {/* Visor de PDF - SIN CUADRO BLANCO */}
      <div className="flex-1 overflow-auto bg-gray-200 flex justify-center items-start p-2">
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={null}
          noData={null}
          error={
            <div className="text-center p-4">
              <p className="text-gray-600 mb-2">No se pudo cargar el PDF</p>
              <Button onClick={handleDownload} size="sm">
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
            </div>
          }
        >
          <Page 
            pageNumber={pageNumber} 
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={null}
            className="shadow-lg"
          />
        </Document>
      </div>

      {/* Botón grande para móviles en la parte inferior */}
      {isMobile && (
        <div className="p-3 bg-white border-t shrink-0">
          <Button 
            onClick={handleShare} 
            className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white"
            size="lg"
          >
            <Printer className="h-5 w-5" />
            Enviar a Impresora
          </Button>
        </div>
      )}
    </div>
  );
}
